import React, { useCallback, useEffect, useState } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import Select from "react-select"
import { FormInput } from "components/Extra/FormInput"
import { FormImageInput } from "components/Extra/FormImageInput"
import { useTranslation } from "react-i18next"

interface SelectOption {
  id: string
  name: string
}

interface AddNewProductProps {
  isOpen: boolean
  toggle: () => void
  initialValues?: {
    productName?: string
    category?: { id: string; name: string } | null
    price?: number
    brand?: string
    productImage?: {
      id: string
      name: string
      file?: File
      preview?: string
    } | null
    latestTag?: string
    tags?: string[]
    barcode?: {
      id: string
      name: string
      file?: File
      preview?: string
    } | null
    stockQuantity?: { id: string; name: string } | null
    lowStock?: number
    phLevel?: number
    soudiumLevel?: number
    composition?: { name: string; value: number }[]
    productDescription?: string
    automaticCouponGeneration?: boolean
    ProductVisibility?: boolean
    pinProductVisibility?: boolean
    couponBasedProduct?: boolean
  }
}

export const AddNewProduct: React.FC<AddNewProductProps> = ({
  isOpen,
  toggle,
  initialValues,
}) => {
  const { t } = useTranslation()

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("0")
  const [openThirdAccordion, setOpenThirdAccordion] = useState("0")
  const [openFourthAccordion, setOpenFourthAccordion] = useState("0")

  const [selectedStockQuantity, setselectedStockQuantity] = useState<{
    id: string
    name: string
  } | null>(null)

  const [selectedCategory, setSelectedCategory] = useState<{
    id: string
    name: string
  } | null>(null)

  // Category options for the select dropdown
  const categoryOptions = [
    { id: "water", name: "Water" },
    { id: "soft-drinks", name: "Soft Drinks" },
    { id: "juices", name: "Juices" },
    { id: "energy-drinks", name: "Energy Drinks" },
    { id: "sports-drinks", name: "Sports Drinks" },
    { id: "dairy", name: "Dairy" },
    { id: "tea", name: "Tea" },
    { id: "coffee", name: "Coffee" },
    { id: "other", name: "Other" },
  ]

  // Handle form reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCategory(null)
    }
  }, [isOpen])

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      productName: initialValues?.productName || "",
      category: initialValues?.category || null,
      price: initialValues?.price || 0,
      brand: initialValues?.brand || "",
      productImage: initialValues?.productImage || null,
      latestTag: initialValues?.latestTag || "",
      tags: initialValues?.tags || [],
      barcode: initialValues?.barcode || null,
      stockQuantity: initialValues?.stockQuantity || null,
      lowStock: initialValues?.lowStock || 0,
      phLevel: initialValues?.phLevel || 0,
      soudiumLevel: initialValues?.soudiumLevel || 0,
      composition: initialValues?.composition || [
        {
          name: "",
          value: 0,
        },
      ],
      productDescription: initialValues?.productDescription || "",
      automaticCouponGeneration: initialValues?.automaticCouponGeneration ?? true,
      ProductVisibility: initialValues?.ProductVisibility ?? true,
      pinProductVisibility: initialValues?.pinProductVisibility ?? true,
      couponBasedProduct: initialValues?.couponBasedProduct ?? true,
    },
    validationSchema: Yup.object({
      productName: Yup.string()
        .trim()
        .required(t("products_productNameValidationMessage") as string),
      category: Yup.object({
        id: Yup.string().required(
          t("products_categoryValidationMessage") as string
        ),
        name: Yup.string().required(
          t("products_categoryValidationMessage") as string
        ),
      }).required(t("products_categoryValidationMessage") as string),
      price: Yup.number()
        .min(1, t("products_minPriceValidationMessage") as string)
        .required(t("products_priceValidationMessage") as string),
      stockQuantity: Yup.object({
        id: Yup.string().required(
          t("products_stockQuantityValidationMessage") as string
        ),
        name: Yup.string().required(
          t("products_stockQuantityValidationMessage") as string
        ),
      }),
    }),
    onSubmit: values => {
      const {
        ProductVisibility,
        automaticCouponGeneration,
        barcode,
        brand,
        category,
        composition,
        lowStock,
        phLevel,
        pinProductVisibility,
        price,
        productDescription,
        productImage,
        productName,
        soudiumLevel,
        stockQuantity,
        tags,
        couponBasedProduct,
      } = values
      console.log(values)

      // TODO: Dispatch
      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
  }, [isOpen])

  const addNewComposition = useCallback(() => {
    validation.setValues({
      ...validation.values,
      composition: [
        ...validation.values.composition,
        {
          name: "",
          value: 0,
        },
      ],
    })
  }, [validation])

  const changeImageHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "barcode" | "productImage"
  ) => {
    if (!e.target.files || e.target.files.length === 0) return

    const file = e.target.files[0]
    const singleImage = {
      id: `${Date.now()}`,
      name: file.name,
      file,
      preview: URL.createObjectURL(file),
    }

    validation.setFieldValue(type, singleImage)
  }

  const removeImageHandler = useCallback((type: "barcode" | "productImage") => {
    validation.setFieldValue(type, null)
  }, [])

  const addNewCategory = useCallback(() => {
    console.log("addNewCategory")
  }, [])

  const addTag = useCallback(() => {
    const trimmed = validation.values.latestTag.trim()
    validation.setFieldValue("tags", [...validation.values.tags, trimmed])
    validation.setFieldValue("latestTag", "")
  }, [validation])

  const removeTag = useCallback((tagToRemove: string) => {
    const tags = validation.values.tags.filter(tag => tag !== tagToRemove)
    validation.setFieldValue("tags", tags)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addTag()
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} className="border-bottom">
        <h5 className="fw-semibold mb-0">
          {t("products_addNewProduct")}
        </h5>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openFirstAccordion} className="shadow-sm rounded-3 mb-3">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("products_name") as string}
                      name="productName"
                      type="text"
                      placeholder={t("products_enterProductName") as string}
                      validation={validation}
                      required
                    />
                  </Col>
                  <Col md="6">
                    <div className="d-flex align-items-end justify-content-start">
                      <div className="flex-grow-1 me-2">
                        <FormGroup>
                          <FormLabel htmlFor="category" required>
                            {t("products_category")}
                          </FormLabel>
                          <Select
                            id="category"
                            name="category"
                            value={selectedCategory}
                            onChange={(option: any) => {
                              setSelectedCategory(option)
                              validation.setFieldValue("category", option)
                            }}
                            options={categoryOptions}
                            getOptionLabel={(option: SelectOption) =>
                              option.name
                            }
                            getOptionValue={(option: SelectOption) => option.id}
                            classNamePrefix="select2-selection"
                            placeholder={
                              t("products_enterProductCategory") as string
                            }
                            isClearable
                            isSearchable
                          />
                          {validation.touched.category &&
                            validation.errors.category && (
                              <FormFeedback className="d-block">
                                {validation.errors.category as string}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </div>
                      <span
                        className="input-group-append mb-3"
                        style={{ cursor: "pointer" }}
                        onClick={addNewCategory}
                      >
                        <i className="bx bx-plus font-size-18 p-2 px-2 input-group-text"></i>
                      </span>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormInput
                      label={t("common_price") as string}
                      lightLabel={t("products_qarCurrency") as string}
                      name="price"
                      type="number"
                      placeholder={t("products_enterProductPrice") as string}
                      validation={validation}
                      required
                      info={t("products_productPriceInfo") as string}
                    />
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_brand") as string}
                      name="brand"
                      type="text"
                      placeholder={t("products_enterBrand") as string}
                      validation={validation}
                      info={t("products_brandInfo") as string}
                    />
                  </Col>
                </Row>
                <FormImageInput
                  name="productImage"
                  validation={validation}
                  changeImageHandler={e =>
                    changeImageHandler(e, "productImage")
                  }
                  info={t("products_productImageInfo") as string}
                  label={t("products_productImage") as string}
                  removeHandler={() => removeImageHandler("productImage")}
                />
                <div>
                  {/* <Input
                    type="text"
                    value={tagValue}
                    onChange={e => setTagValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a tag and press Enter"
                  /> */}
                  <FormInput
                    label={t("products_tag") as string}
                    name="latestTag"
                    type="text"
                    placeholder={t("products_enterTag") as string}
                    validation={validation}
                    info={t("products_tagInfo") as string}
                    onKeyDown={handleKeyDown}
                  />
                  <div className="mt-2">
                    {validation.values.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        color="primary"
                        pill
                        className="me-1 px-3 py-2 font-size-12"
                        style={{ cursor: "pointer" }}
                        onClick={() => removeTag(tag)}
                      >
                        {tag} &times;
                      </Badge>
                    ))}
                  </div>
                </div>

                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="visibility">
                    <span className="fw-bold">
                      {t("products_couponBasedProduct")}
                    </span>{" "}
                    ({t("products_couponBasedProductInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="couponBasedProduct"
                      type="checkbox"
                      className="form-check-input"
                      id="visibility"
                      checked={validation.values.couponBasedProduct}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openSecondAccordion} className="shadow-sm rounded-3 mb-3">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenSecondAccordion(
                    openSecondAccordion === "0" ? "1" : "0"
                  )
                }
              >
                {t("products_inventory")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormImageInput
                  name="barcode"
                  validation={validation}
                  changeImageHandler={e => changeImageHandler(e, "barcode")}
                  label="Internal Item code/Bar-code"
                  removeHandler={() => removeImageHandler("barcode")}
                  icon={
                    <i className="mdi mdi-barcode-scan font-size-20 input-group-text"></i>
                  }
                />
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <FormLabel htmlFor="stockQuantity" required>
                        {t("products_stockQuantity")}
                      </FormLabel>
                      <Select
                        id="stockQuantity"
                        value={selectedStockQuantity}
                        onChange={(
                          selectedOption: { id: string; name: string } | null
                        ) => {
                          setselectedStockQuantity(selectedOption)
                          validation.setFieldValue("stockQuantity", {
                            id: selectedOption?.id,
                            name: selectedOption?.name,
                          })
                        }}
                        options={Array.from(
                          { length: 10 },
                          (_, i) => i + 1
                        ).map(one => ({
                          id: one.toString(),
                          name: one.toString(),
                        }))}
                        getOptionLabel={(option: SelectOption) => option.name}
                        getOptionValue={(option: SelectOption) => option.id}
                        className="select2-selection"
                      />
                      {validation.touched.stockQuantity &&
                      validation.errors.stockQuantity ? (
                        <FormFeedback type="invalid">
                          {validation.errors.stockQuantity}
                        </FormFeedback>
                      ) : null}

                      <div className="d-flex align-items-center gap-1 ms-2">
                        <i className="bx bx-info-circle"></i>
                        <FormFeedback
                          type="invalid"
                          className="d-block text-secondary"
                        >
                          {t("products_availableStockForProduct") as string}
                        </FormFeedback>
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormInput
                      label={t("products_lowStock") as string}
                      name="lowStock"
                      type="number"
                      validation={validation}
                      placeholder={t("products_enterLowStock") as string}
                      info={t("products_lowStockInfo") as string}
                    />
                  </Col>
                </Row>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openThirdAccordion} className="shadow-sm rounded-3 mb-3">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenThirdAccordion(openThirdAccordion === "0" ? "1" : "0")
                }
              >
                {t("products_productDetails")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <div className="d-flex align-items-start gap-1">
                        <FormLabel className="form-label" htmlFor="phLevel">
                          {t("products_PH")}
                        </FormLabel>
                        <span> ({t("products_MgL")})</span>
                      </div>
                      <Input
                        id="phLevel"
                        name="phLevel"
                        type="number"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.phLevel || ""}
                        invalid={
                          validation.touched.phLevel &&
                          validation.errors.phLevel
                            ? true
                            : false
                        }
                        placeholder={t("products_enterPH") as string}
                      />

                      {validation.touched.phLevel &&
                      validation.errors.phLevel ? (
                        <FormFeedback type="invalid">
                          {validation.errors.phLevel}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <div className="d-flex align-items-end justify-content-start">
                        <div className="flex-grow-1 me-2">
                          <div className="d-flex align-items-start gap-1">
                            <FormLabel className="form-label" htmlFor="phLevel">
                              {t("products_sodiumlevel")}
                            </FormLabel>
                            <span> ({t("products_MgL")})</span>
                          </div>
                          <Input
                            id="soudiumLevel"
                            name="soudiumLevel"
                            type="number"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.soudiumLevel || ""}
                            invalid={
                              validation.touched.soudiumLevel &&
                              validation.errors.soudiumLevel
                                ? true
                                : false
                            }
                            placeholder={t("products_enterSodium") as string}
                          />
                          {validation.touched.soudiumLevel &&
                          validation.errors.soudiumLevel ? (
                            <FormFeedback type="invalid">
                              {validation.errors.soudiumLevel}
                            </FormFeedback>
                          ) : null}
                        </div>{" "}
                        <span
                          className="input-group-append"
                          style={{ cursor: "pointer" }}
                          onClick={addNewComposition}
                        >
                          <i className="bx bx-plus font-size-18 p-1 px-2 input-group-text"></i>
                        </span>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                {validation.values.composition.map((_, index) => (
                  <Row key={index}>
                    <Col md="6">
                      <FormGroup>
                        <FormLabel
                          className="form-label"
                          htmlFor={`composition[${index}].name`}
                        >
                          {t("products_compositionName")}
                        </FormLabel>
                        <Input
                          id={`composition[${index}].name`}
                          name={`composition[${index}].name`}
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={
                            validation.values.composition[index].name || ""
                          }
                          placeholder={
                            t("products_enterCompositionName") as string
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <div className="d-flex align-items-start gap-1">
                          <FormLabel
                            className="form-label"
                            htmlFor={`composition[${index}].value`}
                          >
                            {t("products_compositionValue")}
                          </FormLabel>
                          <span> ({t("products_MgL")})</span>
                        </div>
                        <Input
                          id={`composition[${index}].value`}
                          name={`composition[${index}].value`}
                          type="number"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={
                            validation.values.composition[index].value || ""
                          }
                          placeholder={
                            t("products_enterCompositionValue") as string
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                ))}
                <FormInput
                  label={t("products_productDescription") as string}
                  name="productDescription"
                  type="textarea"
                  rows={3}
                  validation={validation}
                  placeholder={t("products_enterProductDescription") as string}
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Accordion open={openFourthAccordion} className="shadow-sm rounded-3">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFourthAccordion(
                    openFourthAccordion === "0" ? "1" : "0"
                  )
                }
              >
                {t("products_visibility")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="visibility">
                    <span className="fw-bold">
                      {t("products_couponGeneration")}
                    </span>{" "}
                    ({t("products_couponGenerationInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="automaticCouponGeneration"
                      type="checkbox"
                      className="form-check-input"
                      id="visibility"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      checked={validation.values.automaticCouponGeneration}
                    />
                  </div>
                </FormGroup>
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="visibility">
                    <span className="fw-bold">
                      {t("products_productVisibility")}
                    </span>{" "}
                    ({t("products_productVisibilityInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="ProductVisibility"
                      type="checkbox"
                      className="form-check-input"
                      id="visibility"
                      checked={validation.values.ProductVisibility}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </FormGroup>
                <FormGroup className="d-flex align-items-center gap-3">
                  <FormLabel htmlFor="visibility">
                    <span className="fw-bold">{t("products_pinProduct")}</span>{" "}
                    ({t("products_pinProductInfo")})
                  </FormLabel>
                  <div className="form-check form-switch mb-2">
                    <input
                      name="pinProductVisibility"
                      type="checkbox"
                      className="form-check-input"
                      id="visibility"
                      checked={validation.values.pinProductVisibility}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>

          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className="btn bg-gradiant-blue text-white border-0"
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {t("products_createProduct")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-light border"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
